import React, { useState, useEffect, useRef } from 'react'
import { map } from 'lodash'
import styled from 'styled-components'
import { Formik, FormikProps } from 'formik'
import * as Yup from 'yup'
import { useRouter } from 'next/router'
import Turnstile from 'react-turnstile'

import { useCardViewportScaleAnimation } from '../../src/utils/hooks/use-card-viewport-scale-animation'
import RichText from '../../src/components/RichText'
import CardContainer from '../../src/components/CardContainer'
import Input from '../../src/components/Input'
import BasicRoundedButton from '../../src/components/BasicRoundedButton/ButtonLink'
import useLocalization from '../../src/utils/hooks/use-localization'
import { SCALEUP_HOVER, SCALE_DOWN } from '../../src/utils/animation-config'
import { createAlert } from '../../src/providers/notifications/NotificationsProvider'
import Loader from '../../src/components/Loader'
import { devices, media } from '../../src/utils/style-utils'
import sendOfferForm, { offerFormSubmitType } from '../../src/utils/form-utils'
import MultipleCheckbox from '../../src/components/Checkbox/MultipleCheckbox'
import { RequestOfferFormSlice } from '../../prismicio-types'
import { backgroundColors } from '../../src/styles/backgroundColors'
import RadioButtons from '../../src/components/RadioButtons/RadioButtons'
import {
	convertRandomMathExpressionToString,
	generateRandomMathExpression,
	calculateCorrectAnswer,
} from '../../src/utils/general-helpers'

const RequestOfferForm = ({ slice }: { slice: RequestOfferFormSlice }) => {
	const cardAnimationProps = useCardViewportScaleAnimation()
	const backgroundColor = backgroundColors(slice.primary.background_color ?? 'Baby Blue')
	const { translate } = useLocalization()
	const router = useRouter()
	const formikRef = useRef<FormikProps<any>>(null)
	const [randomMathExpression, setRandomMathExpression] = useState<string>()
	const [isTurnstilePassed, setIsTurnstilePassed] = useState(false)
	const [token, setToken] = useState<string>()

	useEffect(() => {
		setRandomMathExpression(generateRandomMathExpression())
	}, [])

	return (
		<Container
			{...cardAnimationProps}
			options={{ backgroundColor }}
		>
			<InfoColumn>
				<RichText content={slice.primary.title} />
				<RequestInfo>
					{map(slice.items, (item, index) => (
						<RichText
							content={item.content_option}
							key={index}
						/>
					))}
				</RequestInfo>
			</InfoColumn>
			<FormColumn>
				<Formik
					innerRef={formikRef}
					initialValues={{
						ssn: '',
						name: '',
						email: '',
						phone: '',
						product: [] as ('Posi' | 'Veflausn')[],
						estimated_turnover: '',
						estimated_average_transaction: '',
						product_or_service_type: '',
						organization_website: '',
						other_details: '',
						pricing_type: '',
						other_platform: '',
						platform_selection: [] as string[],
						recaptcha: '',
					}}
					validationSchema={Yup.object().shape({
						ssn: Yup.string()
							.required()
							.matches(/^[0-9]{6}-?[0-9]{4}$/, 'invalid ssn'),
						name: Yup.string().required(),
						email: Yup.string().email().required(),
						phone: Yup.string().required(),
						product: Yup.array()
							.of(Yup.string())
							.min(1, '')
							.test('something-selected', 'hello', (value, ctx) => {
								return !!(value && value.length > 0)
							})
							.required('Required'),
						platform_selection: Yup.array()
							.when('product', {
								is: (product) => product && product.includes('Veflausn'),
								then: Yup.array().of(Yup.string()).min(1, 'Platform is required').required(),
							})
							.default([]),
						estimated_turnover: Yup.number().required(),
						estimated_average_transaction: Yup.number().required(),
						product_or_service_type: Yup.string().required(),
						organization_website: Yup.string().matches(
							/^((https?):\/\/)?(www.)?[a-z0-9]+(\.[a-z]{2,}){1,3}(#?\/?[a-zA-Z0-9#]+)*\/?(\?[a-zA-Z0-9-_]+=[a-zA-Z0-9-%]+&?)?$/,
							'invalid url'
						),
						other_details: Yup.string(),
						pricing_type: Yup.string().required(),
						recaptcha: Yup.number()
							.test('recaptcha', 'Rangt svar', (value) => {
								const correctAnswer = calculateCorrectAnswer(randomMathExpression!)
								return value?.toString() === correctAnswer
							})
							.required('Vinsamlega sláðu inn rétt svar við stærð\u00adfræði\u00adspurning\u00adunni'),
					})}
					onSubmit={async (val, formikBag) => {
						if (!isTurnstilePassed)
							return createAlert({
								title: translate('requestOfferForm.error.title'),
								message: translate('requestOfferForm.error.message'),
								type: 'error',
							})
						let newVal = {}
						if (val.product.includes('Veflausn')) {
							if (val.platform_selection.includes(translate('other')))
								newVal = {
									...val,
									platform_selection: [...val.platform_selection, `${translate('other')} - ${val.other_platform}`],
								}
							else newVal = val
						} else {
							newVal = {
								...val,
								platform_selection: [],
							}
						}

						const { ok } = await sendOfferForm({
							randomMathExpression: randomMathExpression,
							token: token,
							...(newVal as offerFormSubmitType),
						})
						if (!ok) {
							return createAlert({
								title: translate('requestOfferForm.error.title'),
								message: translate('requestOfferForm.error.message'),
								type: 'error',
							})
						}
						formikBag.resetForm()
						formikRef.current!.values = {}
						return createAlert({
							title: translate('requestOfferForm.success.title'),
							message: translate('requestOfferForm.success.message'),
							type: 'success',
						})
					}}
				>
					{(formikProps) => {
						const { isSubmitting } = formikProps
						const isDisabled = isSubmitting
						return (
							<Form
								onReset={formikProps.handleReset}
								onSubmit={formikProps.handleSubmit}
							>
								<InputSectionTitle>{translate('requestOfferForm.inputSectionTitle')}</InputSectionTitle>
								<Input
									name='ssn'
									type='text'
									placeholder={''}
									label={translate('requestOfferForm.ssn')}
									disabled={isDisabled}
									isRequired={true}
									onChange={(e) => {
										e.preventDefault()
										const { value } = e.target
										const regex = new RegExp('^[0-9]{0,6}-?[0-9]{0,4}$')
										if (regex.test(value)) {
											formikProps.setFieldValue('ssn', value)
										}
									}}
								/>
								<Input
									name='name'
									type='text'
									placeholder={''}
									label={translate('requestOfferForm.name')}
									disabled={isDisabled}
									isRequired={true}
								/>
								<Input
									name='email'
									type='email'
									placeholder={''}
									label={translate('requestOfferForm.email')}
									disabled={isDisabled}
									isRequired={true}
								/>
								<Input
									name='phone'
									type='tel'
									placeholder={''}
									label={translate('requestOfferForm.phone')}
									disabled={isDisabled}
									isRequired={true}
									onChange={(e) => {
										e.preventDefault()
										const { value } = e.target
										const regex = /^([0-9]|\+|-|\s){0,15}$/
										if (regex.test(value)) {
											formikProps.setFieldValue('phone', value)
										}
									}}
								/>
								<Input
									name='estimated_turnover'
									type='number'
									placeholder={''}
									label={translate('requestOfferForm.estimated_turnover')}
									disabled={isDisabled}
									isRequired={true}
								/>
								<Input
									name='estimated_average_transaction'
									type='number'
									placeholder={''}
									label={translate('requestOfferForm.estimated_average_transaction')}
									disabled={isDisabled}
									isRequired={true}
								/>
								<Input
									name='product_or_service_type'
									type='text'
									placeholder={''}
									label={translate('requestOfferForm.product_or_service_type')}
									disabled={isDisabled}
									isRequired={true}
								/>
								<Input
									name='organization_website'
									type='text'
									placeholder={''}
									label={translate('requestOfferForm.organization_website')}
									disabled={isDisabled}
									isRequired={false}
								/>
								<Input
									name='other_details'
									type='textarea'
									placeholder={translate('requestOfferForm.other_details')}
									disabled={isDisabled}
									isRequired={false}
								/>
								<RadioButtons
									name='pricing_type'
									values={translate('requestOfferForm.pricing_typeOptions')}
									isRequired={true}
									groupLabel={translate('requestOfferForm.pricing_type')}
									disclaimerRefrence={slice.primary.pricing_disclaimer && slice.primary.pricing_disclaimer.length > 0}
									formikProps={formikProps}
								/>
								{slice.primary.pricing_disclaimer && <DisclaimerRichText content={slice.primary.pricing_disclaimer} />}
								<MultipleCheckbox
									name={'product'}
									labels={['Posi', 'Veflausn']}
									isRequired={true}
									groupLable={translate('requestOfferForm.checkBoxGroupLable')}
								/>
								{formikProps.values.product.includes('Veflausn') && (
									<MultipleCheckbox
										name={'platform_selection'}
										labels={translate('requestOfferForm.checkButtonsOptional')}
										isRequired={true}
										groupLable={translate('requestOfferForm.platform_selection')}
										otherTextArea={true}
										otherPlatformName='other_platform'
									/>
								)}
								{formikProps.errors.product && <span>Vinsamlegast veljið að minnsta kosti eina greiðslulausn</span>}
								<Input
									name='recaptcha'
									label={convertRandomMathExpressionToString(randomMathExpression, router.locale)}
									type='number'
									placeholder={''}
									disabled={isDisabled}
									isRequired={true}
								/>
								<Turnstile
									sitekey={process.env.CLOUDFLARE_TURNSTILE_SITE_KEY ?? ''}
									theme='light'
									onVerify={(token) => {
										setToken(token)
										setIsTurnstilePassed(true)
									}}
								/>
								<Button
									type='submit'
									disabled={isSubmitting}
									whileTap={!isSubmitting && SCALE_DOWN}
									whileHover={!isSubmitting && SCALEUP_HOVER}
									isSubmitting={isSubmitting}
								>
									{isSubmitting ? <Loader /> : translate('requestOfferForm.submitButton')}
								</Button>
							</Form>
						)
					}}
				</Formik>
				<MobileRequestInfo>
					{map(slice.items, (item, index) => (
						<RichText
							content={item.content_option}
							key={index}
						/>
					))}
				</MobileRequestInfo>
			</FormColumn>
		</Container>
	)
}

export default RequestOfferForm

const CheckboxGroupName = styled.div`
	margin: 0;
	height: min-content;
	margin-top: 16px;

	h5 {
		margin: 0;
	}
`

const InputSectionTitle = styled.h5`
	margin: 0;
	margin-bottom: 8px;
`

const Container = styled(CardContainer)`
	height: 100%;

	${devices.safariDesktop`
		height: fit-content;
		max-height: unset;
	`}

	${media.tablet`
		display: flex;
		flex-direction: column;
		padding: 12% 7%;
		gap: var(--spacer-md);
	`}
`

const Button = styled(BasicRoundedButton)`
	justify-content: center;
	color: var(--neon);
	margin-top: 32px;
	:disabled {
		cursor: wait;
		background-color: var(--blue-300);
	}

	${media.desktopHuge`
		height: 72px;
		border-radius: 72px;
	`}
`

const RequestInfo = styled.div`
	margin-top: var(--spacer-md);
	display: flex;
	gap: 70px;

	${media.tablet`
		display: none;
	`}
`

const MobileRequestInfo = styled.div`
	display: none;
	${media.tablet`
		margin: 0;
		display: flex;
		gap: var(--spacer-md);
		p {
			margin: 0;
		}
	`}
`

const InfoColumn = styled.div`
	grid-column: 2 / span 4;
	display: flex;
	flex-direction: column;
	justify-content: space-between;

	margin-top: var(--spacer-base);
	margin-bottom: var(--spacer-base);

	${media.desktopSm`
		grid-column: 2 / span 3;
	`};

	${media.tablet`
		margin: 0;
	`}

	* > h1 {
		margin-top: 0;
		${media.tablet`
			font-size: clamp(2.5rem, 3vw + 1rem, 8.75rem);
			margin: 0.5rem 0rem;
			line-height: clamp(110%, 3vw + 120%, 130%);
		`};
	}

	* > p {
		margin: 0;
	}
`

const FormColumn = styled.div`
	margin-top: var(--spacer-base);
	margin-bottom: var(--spacer-base);
	grid-column: 7 / -2;
	display: flex;
	flex-direction: column;
	gap: var(--spacer-xs);

	${media.desktopSm`
		grid-column: 5 / -2;
	`};

	${media.tablet`
		margin: 0;
	`}

	* > textarea {
		height: 12rem;
	}

	${media.desktopHuge`
		grid-column: 6 / -2;

		* > textarea {
		height: 16rem;
		}

	`}
`

const Form = styled.form`
	display: contents;
`
const DisclaimerRichText = styled(RichText)`
	& p {
		font-size: 16px;
		line-height: 32px;
		margin-block: 0;

		${media.phone`
			font-size: 16px;
			line-height: 24px;
		`}
	}
`
